import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Box, Text } from "@mantine/core";
import { AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useContentBlockTimer, useMediaPreload } from "@/hooks";
import { DisplayContentAssignment, ContentBlock, Venue } from "@/utils/api";
import { checkContentBlock, collectCacheableMediaUrls } from "@/utils";
import { PlayListItem } from "@/utils/api";
import { ContentBlockTransition } from "@/components/generic/ContentBlockTransition";

import { ContentBlockRenderer } from "../ContentBlockRenderer";
import { ProgressIndicator } from "./ProgressIndicator";
import { ErrorDisplay } from "./ErrorDisplay";
import { BlockTransitionAnimationType } from "../BlockTransitionAnimationSelector";
import { ProgressStyleValue } from "../ProgressStyleSelector";

// Initialize dayjs UTC plugin
dayjs.extend(utc);

export interface ContentBlockAreaProps {
  title?: string | null | undefined;

  /** Array of display content assignments to display (legacy) */
  displayContentAssignments?: DisplayContentAssignment[];

  /** Array of playlist items (new zone-based system) */
  playListItems?: PlayListItem[];

  zoneId?: string;

  /** Venue data for content blocks that need it */
  venue?: Venue;

  /** Default duration in seconds if not specified in assignment or content block */
  defaultDurationSeconds?: number;

  /** Additional CSS class name for styling */
  className?: string;

  /** Whether to automatically start the rotation */
  autoStart?: boolean;

  /** Transition type between content blocks */
  transitionType?: BlockTransitionAnimationType;

  /** Transition duration in milliseconds */
  transitionDuration?: number;

  /** Whether to show progress indicator */
  showProgress?: boolean;

  /** Progress indicator position */
  progressPosition?: "bottom" | "top";

  /** Progress indicator style */
  progressStyle?: ProgressStyleValue;

  themeConfig?: any;

  /** Callback fired when content block changes */
  onContentBlockChange?: (contentBlock: ContentBlock, index: number) => void;

  /** Callback fired when a content block loads */
  onContentBlockLoad?: (contentBlock: ContentBlock) => void;

  /** Callback fired when a content block fails to load */
  onContentBlockError?: (contentBlock: ContentBlock, error: string) => void;
}

export const ContentBlockArea: React.FC<ContentBlockAreaProps> = ({
  title,
  displayContentAssignments,
  playListItems,
  zoneId,
  venue,
  defaultDurationSeconds = 30,
  className,
  autoStart = true,
  transitionType = "flip",
  transitionDuration = 500,
  showProgress = true,
  progressPosition = "bottom",
  progressStyle = "bar",
  themeConfig,
  onContentBlockChange,
  onContentBlockLoad,
  onContentBlockError,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isActive] = useState(autoStart);
  const [timeProgress, setTimeProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(() => dayjs());
  const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Convert playListItems to displayContentAssignments format
  const convertedAssignments = useMemo((): DisplayContentAssignment[] => {
    // If playListItems provided, use them (new system)
    if (playListItems && playListItems.length > 0) {
      return playListItems
        .filter((item) => item.contentBlock) // Only items with content blocks
        .map((item) => ({
          id: item.id,
          displayId: undefined,
          contentBlockId: item.contentBlockId,
          startDatetime: item.startDatetime,
          endDatetime: item.endDatetime,
          durationSeconds: item.durationSeconds,
          orderNo: item.orderNo,
          contentBlock: item.contentBlock,
        })) as DisplayContentAssignment[];
    }

    // Fall back to displayContentAssignments (legacy system)
    return displayContentAssignments || [];
  }, [playListItems, displayContentAssignments]);

  // Eagerly preload every cacheable media file in this zone (across ALL items,
  // regardless of their current time window) into the session media cache. This
  // downloads uploaded images/videos once, up front, so the display keeps
  // rotating smoothly even if the network later becomes slow or unavailable.
  const mediaUrlsToPreload = useMemo(
    () =>
      collectCacheableMediaUrls(
        convertedAssignments.map((assignment) => assignment.contentBlock),
      ),
    [convertedAssignments],
  );
  useMediaPreload(mediaUrlsToPreload);

  // Update current time every minute to check for time-based content changes
  useEffect(() => {
    timeIntervalRef.current = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000); // Update every minute

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, []);

  // Function to get currently visible content blocks
  const getVisibleContentBlocks = useCallback(
    (checkTime: dayjs.Dayjs) => {
      if (!convertedAssignments || convertedAssignments.length === 0) {
        return [];
      }

      // Convert checkTime to local timezone for consistent comparison
      const localCheckTime = checkTime.local();

      // Filter assignments that are currently active and have content blocks
      const validAssignments = convertedAssignments.filter((assignment) =>
        checkContentBlock(localCheckTime, assignment),
      );

      // Transform to content blocks with enhanced duration info
      const contentBlocksWithDuration = validAssignments.map((assignment) => ({
        ...assignment.contentBlock!,
        durationSeconds:
          assignment.durationSeconds ||
          assignment.contentBlock!.durationSeconds ||
          defaultDurationSeconds,
        orderNo: assignment.orderNo || assignment.contentBlock!.orderNo || 0,
      }));

      return contentBlocksWithDuration;
    },
    [convertedAssignments, defaultDurationSeconds],
  );

  // Sort content blocks by orderNo and filter by time
  const sortedContentBlocks = useMemo(() => {
    return getVisibleContentBlocks(currentTime);
  }, [getVisibleContentBlocks, currentTime]);

  // Get current content block
  const currentContentBlock = useMemo(() => {
    if (sortedContentBlocks.length === 0) return null;
    return sortedContentBlocks[currentIndex] || null;
  }, [sortedContentBlocks, currentIndex]);

  // Get current duration for timer
  const currentDuration = useMemo(() => {
    if (!currentContentBlock) return 0;
    return Math.max(currentContentBlock.durationSeconds || 10, 1); // Minimum 1 second
  }, [currentContentBlock]);

  // Move to next content block
  const goToNext = useCallback(() => {
    // Get the most current visible blocks at the time of transition
    const currentVisibleBlocks = getVisibleContentBlocks(dayjs.utc());

    if (currentVisibleBlocks.length <= 1) return;

    let nextIndex = (currentIndex + 1) % currentVisibleBlocks.length;

    // If the current block is no longer visible, find the next visible one
    const currentBlockStillVisible = currentVisibleBlocks.some(
      (block) => block.id === currentContentBlock?.id,
    );

    if (!currentBlockStillVisible) {
      // Current block is no longer visible, start from the beginning
      nextIndex = 0;
    }

    setCurrentIndex(nextIndex);
    setTimeProgress(0); // Reset progress

    const nextContentBlock = currentVisibleBlocks[nextIndex];

    if (nextContentBlock) {
      onContentBlockChange?.(nextContentBlock, nextIndex);
    }
  }, [
    currentIndex,
    currentContentBlock?.id,
    getVisibleContentBlocks,
    onContentBlockChange,
  ]);

  // Content block timer with progress tracking
  const isUnsplashBlock =
    currentContentBlock?.contentType?.toLowerCase() === "unsplash_collection";

  const { startTimer, stopTimer } = useContentBlockTimer({
    durationSeconds: currentDuration,
    onTimerExpire: goToNext,
    isActive: isActive && sortedContentBlocks.length > 1 && !isUnsplashBlock,
    autoRestart: false,
    blockKey: `${zoneId}-${currentContentBlock?.id}`, // This will force timer restart when block changes
    onTick: (remainingTime, totalTime) => {
      // Update progress (0-100)
      const progress = ((totalTime - remainingTime) / totalTime) * 100;
      setTimeProgress(Math.min(100, Math.max(0, progress)));
    },
    tickInterval: 50,
  });

  // Handle content block load
  const handleContentBlockLoad = useCallback(() => {
    if (currentContentBlock) {
      onContentBlockLoad?.(currentContentBlock);
    }
  }, [currentContentBlock, onContentBlockLoad]);

  // Handle content block error
  const handleContentBlockError = useCallback(
    (error: string) => {
      if (currentContentBlock) {
        onContentBlockError?.(currentContentBlock, error);
      }
    },
    [currentContentBlock, onContentBlockError],
  );

  // Handle video play/pause for timer control
  const handleVideoPlay = useCallback(() => {
    // Pause timer when video is playing (let video control timing)
    stopTimer();
  }, [stopTimer]);

  const handleVideoPause = useCallback(() => {
    // Resume timer when video is paused
    if (isActive && sortedContentBlocks.length > 1) {
      startTimer();
    }
  }, [startTimer, isActive, sortedContentBlocks.length]);

  const handleVideoEnded = useCallback(() => {
    // Resume timer when video is paused
    if (isActive && sortedContentBlocks.length > 1) {
      goToNext();
    }
  }, [goToNext, isActive, sortedContentBlocks.length]);

  // Memoize the transition + renderer subtree so it is NOT re-created on every
  // progress-bar tick (setTimeProgress fires every 50ms). Re-rendering the
  // AnimatePresence subtree mid-transition makes framer-motion restart the
  // in-flight animation on each tick, so its exit never completes — which leaves
  // the outgoing block stuck and stalls the whole rotation (most visible with
  // non-media blocks like a message, whose background doesn't hide the stall).
  // Every dependency here is stable across progress ticks and only changes when
  // the current block actually changes.
  const transitionContent = useMemo(() => {
    if (!currentContentBlock) return null;
    return (
      <ContentBlockTransition
        transitionKey={currentContentBlock.id}
        duration={transitionDuration}
        type={transitionType}
        className="content-block-area__transition"
      >
        <ContentBlockRenderer
          contentBlock={currentContentBlock}
          className="content-block-area__renderer"
          venue={venue}
          onLoad={handleContentBlockLoad}
          onError={handleContentBlockError}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnded}
          themeConfig={themeConfig}
          totalBlocks={sortedContentBlocks.length}
          transitionType={transitionType}
          transitionDuration={transitionDuration}
        />
      </ContentBlockTransition>
    );
  }, [
    currentContentBlock,
    transitionDuration,
    transitionType,
    venue,
    themeConfig,
    sortedContentBlocks.length,
    handleContentBlockLoad,
    handleContentBlockError,
    handleVideoPlay,
    handleVideoPause,
    handleVideoEnded,
  ]);

  // Reset index when content blocks change or current block becomes invalid
  useEffect(() => {
    if (sortedContentBlocks.length === 0) {
      setCurrentIndex(0);
      return;
    }

    // Check if current index is out of bounds
    if (currentIndex >= sortedContentBlocks.length) {
      setCurrentIndex(0);
      return;
    }

    // Check if the current content block is still in the visible list
    const currentBlock = sortedContentBlocks[currentIndex];
    const currentBlockStillExists =
      currentBlock &&
      sortedContentBlocks.some((block) => block.id === currentBlock.id);

    if (!currentBlockStillExists && sortedContentBlocks.length > 0) {
      // Current block no longer exists, reset to first available block
      setCurrentIndex(0);
    }
  }, [sortedContentBlocks, currentIndex]);

  // Trigger initial content block change callback
  useEffect(() => {
    if (currentContentBlock) {
      onContentBlockChange?.(currentContentBlock, currentIndex);
    }
  }, [currentContentBlock, currentIndex, onContentBlockChange]);

  // Reset progress when current content block changes
  useEffect(() => {
    setTimeProgress(0);
  }, [currentContentBlock?.id, currentIndex]);

  // Handle empty content blocks
  if (!sortedContentBlocks || sortedContentBlocks.length === 0) {
    return <ErrorDisplay type="no-content" className={className} />;
  }

  // Handle invalid current content block
  if (!currentContentBlock) {
    return <ErrorDisplay type="load-error" className={className} />;
  }

  return (
    <Box
      className={`content-block-area ${className || ""}`}
      h="100%"
      style={{
        position: "relative",
        overflow: "hidden",
      }}
      data-zone-id={zoneId}
      data-current-index={currentIndex}
      data-total-blocks={sortedContentBlocks.length}
      data-current-block-id={currentContentBlock.id}
      data-source={playListItems ? "playlist" : "assignments"}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {title && (
          <Box className="content-block-area__header" pb="xs" pt="md">
            <Text
              size="xl"
              pb="sm"
              fw={600}
              className="content-block-area__title"
              ta="center"
              style={{
                textTransform: "uppercase",
                color: "inherit",
                fontFamily: "inherit",
                fontSize: "inherit",
              }}
            >
              {title}
            </Text>
          </Box>
        )}
        <Box
          style={{
            flex: "1 1 0%",
            justifySelf: "stretch",
          }}
        >
          {transitionContent}
        </Box>
        {/* Progress Indicator */}
        {progressStyle != "none" && (
          <AnimatePresence>
            <ProgressIndicator
              show={showProgress}
              timeProgress={timeProgress}
              currentIndex={currentIndex}
              totalBlocks={sortedContentBlocks.length}
              position={progressPosition}
              style={progressStyle}
            />
          </AnimatePresence>
        )}
      </Box>

      {/* Debug info in development */}
      {/* {process.env.NODE_ENV === "development" &&
        sortedContentBlocks.length > 1 && (
          <Box
            className="content-block-area__debug"
            style={{
              position: "absolute",
              top: theme.spacing.xs,
              left: theme.spacing.xs,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              padding: theme.spacing.xs,
              borderRadius: theme.radius.xs,
              fontSize: theme.fontSizes.xs,
              fontFamily: theme.fontFamilyMonospace,
              zIndex: 1000,
            }}
          >
            {zoneId && `Zone: ${zoneId} | `}
            Block {currentIndex + 1}/{sortedContentBlocks.length} (
            {currentDuration}s) - Progress: {Math.round(timeProgress)}% - ID:{" "}
            {currentContentBlock.id} - Source:{" "}
            {playListItems ? "PlayList" : "Assignments"}
          </Box>
        )} */}
    </Box>
  );
};
